import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  BookmarkCollection,
  CREATE_COLLECTION,
  UPDATE_COLLECTION,
  CreateCollectionQuery,
  UpdateCollectionQuery,
} from '../../api';
import * as S from './NameForm.styled';
import { Button } from '@/components/UI/Button/Button';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';

interface NameFormProps {
  noGutters?: boolean;
  onSuccess: () => void;
  mode?: 'edit' | 'create';
  collection?: BookmarkCollection | null;
}

export const NameForm = ({
  onSuccess,
  collection,
  mode = 'create',
  noGutters = false,
}: NameFormProps) => {
  const { t } = useTranslation();

  const [updateCollection, { loading: isUpdating }] =
    useMutation<UpdateCollectionQuery>(UPDATE_COLLECTION);
  const [createCollection, { loading: isCreating }] =
    useMutation<CreateCollectionQuery>(CREATE_COLLECTION);

  const [name, setName] = useState(collection?.name || '');
  const [error, setError] = useState<string | null>(null);

  const isEditMode = mode === 'edit';
  const isLoading = isUpdating || isCreating;
  const buttonTitle = isEditMode ? t('common.save') : t('common.create');

  const handleSubmit = async () => {
    setError('');

    if (isEditMode) {
      try {
        const variables = { name, bookmarkCollectionId: collection?.id };
        const { data } = await updateCollection({ variables });

        if (data?.updateBookmarkCollection?.errors?.length) {
          setError(data.updateBookmarkCollection.errors[0].message);
          return;
        }

        onSuccess();
        setTimeout(() => setName(''), MODAL_ANIMATION_DURATION);
      } catch (_) {
        setError(t('bookmark_collections.universal_error_message'));
      }
    } else {
      const { data } = await createCollection({ variables: { name } });

      if (data?.createBookmarkCollection?.errors?.length) {
        setError(data.createBookmarkCollection.errors[0].message);
        return;
      }

      onSuccess();
      setTimeout(() => setName(''), MODAL_ANIMATION_DURATION);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter') handleSubmit();
  };

  return (
    <S.Form $noGutters={noGutters}>
      <S.InputWrapper $hasError={!!error}>
        <S.NameInput
          autoFocus
          value={name}
          maxLength={50}
          hasBorderBottom={false}
          onKeyDown={handleKeyDown}
          label={t('bookmark_collections.form_placeholder')}
          onChange={value => setName(value as string)}
          data-testid="collection-name-input"
        />
      </S.InputWrapper>

      {error && <S.ErrorText>{error}</S.ErrorText>}

      <Button
        isFullWidth
        borderRadius={14}
        appearance="primary"
        onClick={handleSubmit}
        isLoading={isLoading}
        disabled={!name.length || isLoading}
      >
        {buttonTitle}
      </Button>
    </S.Form>
  );
};
